import { BarChart3, DollarSign, Globe, TrendingUp } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { searchTrafficEstimate } from 'sprites-types/workflow/tools/searchTrafficEstimate'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function SearchTrafficEstimateDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(searchTrafficEstimate, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const resultData = Array.isArray(result) ? result : [result]

  // Calculate totals across all locations
  const totalOrganic = resultData.reduce(
    (sum, item) => sum + (item.organic_monthly_traffic_estimate || 0),
    0
  )
  const totalPaid = resultData.reduce(
    (sum, item) => sum + (item.paid_monthly_traffic_estimate || 0),
    0
  )
  const totalCost = resultData.reduce(
    (sum, item) => sum + (item.paid_traffic_monthly_cost_estimate || 0),
    0
  )
  const totalTraffic = totalOrganic + totalPaid

  const domain = resultData[0]?.target_domain || 'Unknown Domain'

  // Calculate traffic score (0-100) based on total monthly traffic
  const getTrafficScore = (traffic: number) => {
    if (traffic >= 1000000) return 100
    if (traffic >= 500000) return 90
    if (traffic >= 100000) return 80
    if (traffic >= 50000) return 70
    if (traffic >= 10000) return 60
    if (traffic >= 5000) return 50
    if (traffic >= 1000) return 40
    if (traffic >= 500) return 30
    if (traffic >= 100) return 20
    if (traffic > 0) return 10
    return 0
  }

  const score = getTrafficScore(totalTraffic)

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-100'
    if (score >= 60) return 'bg-yellow-100'
    return 'bg-red-100'
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      {/* Header */}
      <div className="flex items-center gap-4 border-b border-gray-200/50 bg-white px-4 py-3">
        <div className="flex-shrink-0">
          <div className={`rounded-full p-2 ${getScoreBg(score)}`}>
            <BarChart3 className={`h-5 w-5 ${getScoreColor(score)}`} />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-500" />
            <div className="text-sm font-semibold text-gray-900">Search Traffic Estimate</div>
          </div>
          <div className="truncate text-xs text-gray-600">{domain}</div>
        </div>
        <div className="flex-shrink-0">
          <div className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}</div>
          <div className="text-xs text-gray-500">Score</div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4 p-4">
        {/* Traffic Overview */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="rounded-lg bg-green-50 p-3">
            <div className="mb-1 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
            <div className="text-lg font-semibold text-green-600">{formatNumber(totalOrganic)}</div>
            <div className="text-xs text-green-700">Organic/Month</div>
          </div>
          <div className="rounded-lg bg-blue-50 p-3">
            <div className="mb-1 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-blue-600" />
            </div>
            <div className="text-lg font-semibold text-blue-600">{formatNumber(totalPaid)}</div>
            <div className="text-xs text-blue-700">Paid/Month</div>
          </div>
          <div className="rounded-lg bg-purple-50 p-3">
            <div className="mb-1 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-purple-600" />
            </div>
            <div className="text-lg font-semibold text-purple-600">${formatNumber(totalCost)}</div>
            <div className="text-xs text-purple-700">Cost/Month</div>
          </div>
        </div>

        {/* Regional Breakdown */}
        {resultData.length > 1 && (
          <div>
            <div className="mb-2 text-sm font-semibold text-gray-900">Regional Breakdown</div>
            <div className="space-y-2">
              {resultData.slice(0, 5).map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded bg-gray-50 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium text-gray-700">{item.location}</div>
                    {item.language && (
                      <div className="text-xs text-gray-500">({item.language})</div>
                    )}
                  </div>
                  <div className="flex gap-3 text-xs">
                    <div className="text-green-600">
                      {formatNumber(item.organic_monthly_traffic_estimate)} org
                    </div>
                    <div className="text-blue-600">
                      {formatNumber(item.paid_monthly_traffic_estimate)} paid
                    </div>
                  </div>
                </div>
              ))}
              {resultData.length > 5 && (
                <div className="text-xs text-gray-600">+{resultData.length - 5} more regions</div>
              )}
            </div>
          </div>
        )}

        {/* Traffic Summary */}
        {totalTraffic > 0 && (
          <div>
            <div className="mb-2 text-sm font-semibold text-gray-900">Traffic Summary</div>
            <div className="rounded bg-gray-50 p-3">
              <div className="mb-2 flex justify-between text-sm">
                <span className="text-gray-600">Total Monthly Traffic</span>
                <span className="font-semibold text-gray-900">{formatNumber(totalTraffic)}</span>
              </div>
              {totalOrganic > 0 && (
                <div className="mb-2 flex justify-between text-sm">
                  <span className="text-green-600">Organic Traffic</span>
                  <span className="font-semibold text-green-700">
                    {formatNumber(totalOrganic)} ({Math.round((totalOrganic / totalTraffic) * 100)}
                    %)
                  </span>
                </div>
              )}
              {totalPaid > 0 && (
                <div className="mb-2 flex justify-between text-sm">
                  <span className="text-blue-600">Paid Traffic</span>
                  <span className="font-semibold text-blue-700">
                    {formatNumber(totalPaid)} ({Math.round((totalPaid / totalTraffic) * 100)}%)
                  </span>
                </div>
              )}
              {totalCost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-purple-600">Monthly Ad Spend</span>
                  <span className="font-semibold text-purple-700">
                    ${totalCost.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Traffic Message */}
        {totalTraffic === 0 && (
          <div className="rounded bg-yellow-50 p-3">
            <div className="text-sm text-yellow-800">
              No significant traffic data found for this domain. This could indicate:
            </div>
            <ul className="mt-1 list-inside list-disc text-xs text-yellow-700">
              <li>Domain has very low search visibility</li>
              <li>Domain is new or not indexed properly</li>
              <li>Traffic data not available in selected region</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

function downloadSearchTrafficEstimate(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function searchTrafficEstimateToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(searchTrafficEstimate, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const resultData = Array.isArray(result) ? result : [result]

  const totalOrganic = resultData.reduce(
    (sum, item) => sum + (item.organic_monthly_traffic_estimate || 0),
    0
  )
  const totalPaid = resultData.reduce(
    (sum, item) => sum + (item.paid_monthly_traffic_estimate || 0),
    0
  )
  const totalCost = resultData.reduce(
    (sum, item) => sum + (item.paid_traffic_monthly_cost_estimate || 0),
    0
  )
  const totalTraffic = totalOrganic + totalPaid

  const domain = resultData[0]?.target_domain || 'Unknown Domain'

  const formatNumber = (num: number) => {
    return num.toLocaleString()
  }

  let markdown = `## Search Traffic Estimate

**Domain:** ${domain}
**Total Monthly Traffic:** ${formatNumber(totalTraffic)}

### Summary
- 🔍 Organic Traffic: ${formatNumber(totalOrganic)} (${totalTraffic > 0 ? Math.round((totalOrganic / totalTraffic) * 100) : 0}%)
- 💰 Paid Traffic: ${formatNumber(totalPaid)} (${totalTraffic > 0 ? Math.round((totalPaid / totalTraffic) * 100) : 0}%)
- 💵 Monthly Ad Spend: $${formatNumber(totalCost)}

`

  if (resultData.length > 1) {
    markdown += `### Regional Breakdown
| Location | Language | Organic Traffic | Paid Traffic | Monthly Cost |
|----------|----------|----------------|--------------|--------------|
`
    resultData.forEach((item) => {
      markdown += `| ${item.location} | ${item.language} | ${formatNumber(item.organic_monthly_traffic_estimate)} | ${formatNumber(item.paid_monthly_traffic_estimate)} | $${formatNumber(item.paid_traffic_monthly_cost_estimate)} |
`
    })
    markdown += '\n'
  }

  return markdown + '---\n'
}

export const SearchTrafficEstimateDisplay = createWorkflowToolComponent(
  SearchTrafficEstimateDisplayComponent,
  downloadSearchTrafficEstimate,
  searchTrafficEstimateToMarkdown
)
